import React from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useLocation } from 'react-router-dom'
import { AppButton } from '@/components'
import { useWithdrawStore } from '@/store/useWithdrawStore'
import { useGetBlockchainsCatalogs } from '@/domain/blockchains/useCases/useGetBlockchainsCatalogs'
import { useGetAboutCurrencyInBlockchain } from '@/domain/currencies/useCases/useGetAboutCurrencyInBlockchain'
import { useValidateFiatAmount } from '@/hooks/useValidateFiatAmount'
import { useCheckTransactionEligibility } from '@/hooks'
import { useGetMaxAmountToWithdraw, MAX_WITHDRAW_TYPE } from '../../hooks'
import { FiatCode } from '@/constants/currencyCodes'

export const WithdrawButtonConfirmation: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()

  const fiatAmount = useWithdrawStore((state) => state.fiatAmount)
  const cryptoAmount = useWithdrawStore((state) => state.cryptoAmount)
  const shakeComponent = useWithdrawStore((state) => state.shakeComponent)
  const selectedCryptoWalletData = useWithdrawStore(
    (state) => state.selectedCryptoWalletData,
  )
  const setFiatAmount = useWithdrawStore((state) => state.setFiatAmount)
  const setCryptoAmount = useWithdrawStore((state) => state.setCryptoAmount)
  const setBlockchain = useWithdrawStore((state) => state.setBlockchain)
  const setIsBlockchainModalOpen = useWithdrawStore(
    (state) => state.setIsBlockchainModalOpen,
  )

  // Получаем список блокчейнов для выбранной валюты
  const { data: blockchains } = useGetBlockchainsCatalogs({
    currency_isin: selectedCryptoWalletData?.code || 'USDT',
  })

  // Получаем максимальную сумму для вывода
  const { maxAmount, maxType } = useGetMaxAmountToWithdraw({
    selectedCryptoWalletData,
  })

  // Получаем информацию о валюте в блокчейне для валидации минимальных сумм
  const {
    data: currencyInBlockchainInfos,
    isLoading: isLoadingCurrencyInBlockchainInfos,
  } = useGetAboutCurrencyInBlockchain({
    currency_isin: selectedCryptoWalletData?.code || 'USDT',
    blockchain_code: blockchains?.blockchains[0]?.code ?? '',
  })

  // Хук валидации суммы
  const { checkTransactionEligibility } = useCheckTransactionEligibility({
    feature: 'withdraw',
  })
  const { validateFiatAmount } = useValidateFiatAmount({
    cryptoCode: selectedCryptoWalletData?.displayCurrencyCode || 'USDT',
    customMaxMessage:
      maxType === MAX_WITHDRAW_TYPE.WALLET_BALANCE
        ? 'form:errors.insufficientBalance'
        : undefined,
    max: {
      amount: String(maxAmount || '0'),
      code: selectedCryptoWalletData?.fiatCurrency as FiatCode,
    },
    min: {
      amount: String(
        currencyInBlockchainInfos?.minWithdrawAmountInFiat.amount || '0',
      ),
      code: currencyInBlockchainInfos?.minWithdrawAmountInFiat.code as FiatCode,
    },
    onInvalidAmount: () => {
      shakeComponent()
      setFiatAmount({
        amount: '0',
        code: fiatAmount.code,
      })
      setCryptoAmount({
        amount: '0',
        code: cryptoAmount.code,
      })
    },
  })

  const redirectToReviewAndConfirm = () => {
    const fromPage = (location.state as any)?.from
    setTimeout(() => navigate('/withdraw/review-and-confirm', {
      state: fromPage ? { from: fromPage } : undefined,
    }), 200)
  }

  const handleWithdrawal = async () => {
    if (isLoadingCurrencyInBlockchainInfos) {
      return
    }

    const isValid = validateFiatAmount({
      fiatAmount,
    })

    if (!isValid) {
      return
    }

    const isEligible = await checkTransactionEligibility({
      amountInUSD: Number(fiatAmount.amount ?? 0),
    })

    if (!isEligible) {
      return
    }

    // Если есть только один блокчейн, сразу переходим к подтверждению
    if (blockchains?.blockchains?.length === 1) {
      setBlockchain(blockchains.blockchains[0])
      return redirectToReviewAndConfirm()
    }

    // Если блокчейнов несколько, открываем модал выбора
    setIsBlockchainModalOpen(true)
  }

  // Проверяем, можно ли нажать кнопку
  const isDisabled =
    !selectedCryptoWalletData ||
    Number(fiatAmount.amount || 0) <= 0 ||
    isLoadingCurrencyInBlockchainInfos

  return (
    <AppButton
      label={t('form:actions.next')}
      onPress={handleWithdrawal}
      disabled={isDisabled}
      type="primaryGradient"
      style={{
        width: '100%',
      }}
    />
  )
}
